// Reusable sortable / filterable table column header.
// Click the header to open a popover with sort + filter options.
// Filter values are unique row values for that column (dropdown style).
// Column filters STACK across columns — each header owns its own entry in
// the parent's filters map. Sorting stacks on top of filters without
// clearing them (parent's rule).
import React, { useMemo, useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ArrowUp, ArrowDown, ArrowUpDown, Filter, Check, X as XIcon } from "lucide-react";

const MUTED = "#6B7A73";
const DARK_GREEN = "#0F3B2E";
const BORDER = "#E5E2DC";

/**
 * Props:
 *   columnKey: string — key of the column (used for sortKey / filterKey).
 *   label: string — visible header text.
 *   type: "text" | "date" — controls sort labels.
 *   values: string[] — unique values to show in the filter dropdown.
 *   view: { sortKey, sortDir, filterKey, filterValue }
 *   onChange(next: partial view)
 *   align: "left" | "right"
 *   testId: prefix for data-testid attrs
 */
export default function TableColumnHeader({
  columnKey,
  label,
  type = "text",
  values = [],
  view,
  onChange,
  align = "left",
  testId,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [filterSearch, setFilterSearch] = useState("");

  // Normalize values into { value, label } pairs.
  const pairs = useMemo(() => (values || []).map((v) => (
    typeof v === "object" && v !== null ? { value: String(v.value), label: String(v.label ?? v.value) } : { value: String(v), label: String(v) }
  )), [values]);

  const sortActive = view?.sortKey === columnKey;
  const activeValues = Array.isArray(view?.filters?.[columnKey])
    ? view.filters[columnKey].map((v) => String(v))
    : [];
  const filterActive = activeValues.length > 0;
  const activeDir = sortActive ? view.sortDir : null;

  const filteredValues = useMemo(() => {
    const q = filterSearch.trim().toLowerCase();
    if (!q) return pairs;
    return pairs.filter((p) => p.label.toLowerCase().includes(q) || p.value.toLowerCase().includes(q));
  }, [pairs, filterSearch]);

  const sortLabels = type === "date"
    ? { asc: "Oldest first", desc: "Newest first" }
    : type === "number"
      ? { asc: "Low → High", desc: "High → Low" }
      : { asc: "A → Z", desc: "Z → A" };

  const applySort = (dir) => {
    // "One at a time" — setting sort clears any filter on other columns implicitly
    // (parent may or may not reset filter; we only touch sort here).
    onChange({ sortKey: columnKey, sortDir: dir });
    setOpen(false);
  };

  const clearSort = () => {
    onChange({ sortKey: null, sortDir: null });
    setOpen(false);
  };

  const toggleFilter = (val) => {
    const s = String(val);
    // This column's current selection — other columns' filters stay stacked.
    const base = activeValues;
    const next = base.includes(s) ? base.filter((v) => v !== s) : [...base, s];
    onChange({ filterKey: columnKey, filterValues: next.length ? next : null });
  };

  const clearFilter = () => {
    onChange({ filterKey: columnKey, filterValues: null });
    setOpen(false);
    setFilterSearch("");
  };

  const arrow = sortActive
    ? (activeDir === "asc" ? <ArrowUp size={11} strokeWidth={2.5} /> : <ArrowDown size={11} strokeWidth={2.5} />)
    : <ArrowUpDown size={11} strokeWidth={2} style={{ opacity: 0.4 }} />;

  return (
    <th className={className} style={{ textAlign: align, overflow: "hidden" }}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={`${testId}-header-${columnKey}`}
            title={`${label} — sort / filter`}
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              maxWidth: "100%",
              background: "none", border: 0, padding: "2px 4px",
              margin: 0, font: "inherit", color: "inherit",
              textTransform: "inherit", letterSpacing: "inherit",
              cursor: "pointer", borderRadius: 4,
              outline: (sortActive || filterActive) ? `1px solid ${DARK_GREEN}` : "none",
              outlineOffset: 2,
            }}
          >
            <span style={{
              overflow: "hidden", textOverflow: "ellipsis",
              whiteSpace: "nowrap", minWidth: 0,
            }}>{label}</span>
            <span style={{ display: "inline-flex", flexShrink: 0 }}>{arrow}</span>
            {filterActive && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 2, color: DARK_GREEN, flexShrink: 0 }}>
                <Filter size={11} strokeWidth={2.5} />
                {activeValues.length > 1 && (
                  <span style={{
                    fontSize: 9, fontWeight: 800, background: DARK_GREEN, color: "#fff",
                    borderRadius: 999, padding: "0 4px", lineHeight: "12px", minWidth: 12,
                    textAlign: "center",
                  }}>{activeValues.length}</span>
                )}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align={align === "right" ? "end" : "start"}
          className="w-56 p-2"
          data-testid={`${testId}-popover-${columnKey}`}
          style={{ zIndex: 60 }}
        >
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".14em",
            textTransform: "uppercase", color: MUTED, padding: "4px 6px" }}>
            Sort
          </div>
          <button
            type="button"
            data-testid={`${testId}-sort-${columnKey}-asc`}
            onClick={() => applySort("asc")}
            style={rowBtn(activeDir === "asc")}
          >
            <ArrowUp size={13} strokeWidth={2.5} />
            <span>{sortLabels.asc}</span>
            {activeDir === "asc" && <Check size={13} strokeWidth={2.5} style={{ marginLeft: "auto", color: DARK_GREEN }} />}
          </button>
          <button
            type="button"
            data-testid={`${testId}-sort-${columnKey}-desc`}
            onClick={() => applySort("desc")}
            style={rowBtn(activeDir === "desc")}
          >
            <ArrowDown size={13} strokeWidth={2.5} />
            <span>{sortLabels.desc}</span>
            {activeDir === "desc" && <Check size={13} strokeWidth={2.5} style={{ marginLeft: "auto", color: DARK_GREEN }} />}
          </button>
          {sortActive && (
            <button
              type="button"
              data-testid={`${testId}-sort-${columnKey}-clear`}
              onClick={clearSort}
              style={{ ...rowBtn(false), color: "#B4351B" }}
            >
              <XIcon size={13} strokeWidth={2.5} />
              <span>Clear sort</span>
            </button>
          )}

          {pairs && pairs.length > 0 && (
            <>
              <div style={{ borderTop: `1px solid ${BORDER}`, margin: "6px 0" }} />
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".14em",
                textTransform: "uppercase", color: MUTED, padding: "4px 6px" }}>
                Filter by value
              </div>
              <input
                data-testid={`${testId}-filter-${columnKey}-search`}
                type="text"
                placeholder="Search values…"
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                style={{
                  width: "100%", height: 30, padding: "0 8px", fontSize: 12,
                  border: `1px solid ${BORDER}`, borderRadius: 6,
                  boxSizing: "border-box", marginBottom: 4,
                }}
              />
              <div style={{ maxHeight: 200, overflowY: "auto" }}>
                {filteredValues.length === 0 && (
                  <div style={{ fontSize: 12, color: MUTED, padding: "6px 8px" }}>
                    No matches
                  </div>
                )}
                {filteredValues.map((p) => {
                  const selected = activeValues.includes(p.value);
                  return (
                    <button
                      key={p.value}
                      type="button"
                      data-testid={`${testId}-filter-${columnKey}-val-${p.value.replace(/\s+/g, "_")}`}
                      onClick={() => toggleFilter(p.value)}
                      style={rowBtn(selected)}
                    >
                      <span
                        aria-hidden
                        style={{
                          width: 14, height: 14, minWidth: 14,
                          borderRadius: 3,
                          border: `1.5px solid ${selected ? DARK_GREEN : "#9AA4A0"}`,
                          background: selected ? DARK_GREEN : "#fff",
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                        }}
                      >
                        {selected && <Check size={10} strokeWidth={3} color="#fff" />}
                      </span>
                      <span style={{
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        maxWidth: 170,
                      }}>{p.label || "—"}</span>
                    </button>
                  );
                })}
              </div>
              {filterActive && (
                <button
                  type="button"
                  data-testid={`${testId}-filter-${columnKey}-clear`}
                  onClick={clearFilter}
                  style={{ ...rowBtn(false), color: "#B4351B", marginTop: 4 }}
                >
                  <XIcon size={13} strokeWidth={2.5} />
                  <span>Clear filter</span>
                </button>
              )}
            </>
          )}
        </PopoverContent>
      </Popover>
    </th>
  );
}

function rowBtn(active) {
  return {
    display: "flex", alignItems: "center", gap: 6,
    width: "100%", padding: "6px 8px", fontSize: 12,
    fontWeight: active ? 700 : 500,
    color: active ? DARK_GREEN : "#1C2B26",
    background: active ? "rgba(15,59,46,0.06)" : "transparent",
    border: 0, borderRadius: 6, cursor: "pointer",
    textAlign: "left", textTransform: "none", letterSpacing: 0,
  };
}
